import { useState } from 'react';

/**
 * The Text size control: default, large (112.5%) and larger (125%).
 *
 * Asked for by the laboratory on 2026-10-03 — the small type was too small and
 * there was no accessibility option. The type scale is in rem (brand.css), so
 * one attribute on the root element scales every size on the page, and so
 * does the reader's own browser setting, which fixed pixels used to override.
 *
 * WHAT IT STORES, AND WHERE. One character ("1" or "2") in this browser's
 * localStorage, under one key shared by the staff application and the client
 * portal. It is a display preference: nothing is sent to the server, nothing
 * is audited, and no record depends on it. A browser that refuses storage
 * (private mode, a locked-down kiosk) still gets the control — the choice
 * simply lasts for the page rather than for the next visit.
 */

const KEY = 'lims.textSize';

/** The steps, in order. "0" is the default and is stored as an absent key. */
const STEPS = [
  { step: '0', name: 'default' },
  { step: '1', name: 'large' },
  { step: '2', name: 'larger' },
] as const;

type Step = (typeof STEPS)[number]['step'];

function isStep(value: string | null): value is Step {
  return value === '0' || value === '1' || value === '2';
}

function stored(): Step {
  try {
    const value = window.localStorage.getItem(KEY);
    return isStep(value) ? value : '0';
  } catch {
    return '0';
  }
}

function apply(step: Step): void {
  const root = document.documentElement;
  if (step === '0') root.removeAttribute('data-text-size');
  else root.setAttribute('data-text-size', step);
}

/**
 * Apply the remembered size. Called once from each entry point BEFORE React
 * renders, so a page does not draw at one size and jump to another.
 */
export function applyStoredTextSize(): void {
  apply(stored());
}

export function TextSize() {
  const [step, setStep] = useState<Step>(stored);

  function choose(next: Step) {
    setStep(next);
    apply(next);
    try {
      if (next === '0') window.localStorage.removeItem(KEY);
      else window.localStorage.setItem(KEY, next);
    } catch {
      // Storage refused: the size still applies to this page.
    }
  }

  return (
    <div className="text-size" role="group" aria-label="Text size">
      {STEPS.map(({ step: value, name }) => (
        <button
          key={value}
          type="button"
          data-text-size={value}
          aria-pressed={step === value}
          aria-label={`Text size: ${name}`}
          onClick={() => choose(value)}
        >
          A
        </button>
      ))}
    </div>
  );
}
